import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface IRequestViewContext {
  requestIds: string[];
  setRequestIds: (ids: string[]) => void;
}

const STORAGE_KEY = 'requestViewIds';

export const RequestViewContext = createContext<IRequestViewContext>({
  requestIds: [],
  setRequestIds: () => {},
});

/**
 * Provider para gerenciar estado de requests para monitoramento.
 * 
 * Mantém uma lista de IDs de requests para monitoramento.
 * Os dados são persistidos no localStorage para sobreviver a navegações.
 */
export const RequestViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [requestIds, setRequestIdsState] = useState<string[]>([]);

  /**
   * Carrega os IDs do localStorage ao montar o componente.
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setRequestIdsState(parsed);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar requestIds do localStorage:', error);
    }
  }, []);

  /**
   * Define os IDs e salva no localStorage.
   */
  const setRequestIds = useCallback((ids: string[]) => {
    setRequestIdsState(ids);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (error) {
      console.error('Erro ao salvar requestIds no localStorage:', error);
    }
  }, []);

  return (
    <RequestViewContext.Provider
      value={{
        requestIds,
        setRequestIds,
      }}
    >
      {children}
    </RequestViewContext.Provider>
  );
};

/**
 * Hook para acessar o contexto de requests.
 * 
 * @returns Contexto de requests
 */
export const useRequestViewContext = (): IRequestViewContext => {
  const context = useContext(RequestViewContext);
  if (!context) {
    throw new Error('useRequestViewContext must be used within RequestViewProvider');
  }
  return context;
};
